import { AfterViewChecked, Directive, ElementRef, Input, OnChanges } from '@angular/core';

/**
 * Mantiene sincronizado el valor de un `<select>` nativo con su binding `[value]`.
 *
 * ## Por qué existe
 *
 * `<select [value]="algo">` NO es fiable en Angular: la asignación a la propiedad `value` del
 * elemento se ejecuta **antes** de que el `@for` haya creado los `<option>`. Si en ese instante la
 * opción todavía no está en el DOM, el navegador ignora la asignación y el select se queda en la
 * **primera opción**.
 *
 * El síntoma en el constructor de workflows era muy visible: al pasar de un paso a otro sin recargar
 * la página, el inspector mostraba `Urgente` cuando lo guardado era `ALTO`, o `MINUTES` cuando lo
 * guardado era `DAYS`, y una condición ya configurada aparecía como si estuviera vacía. El valor
 * real nunca se perdía (se veía en "Modo avanzado (JSON)"), pero el usuario leía información falsa
 * y podía "corregir" lo que no estaba mal.
 *
 * ## Cómo lo arregla
 *
 * Al declarar `value` como `@Input()` de la directiva, Angular deja de escribir la propiedad del DOM
 * y nos cede el control. Reaplicamos el valor en `ngAfterViewChecked`, que corre **después** de que
 * las opciones existan, y solo cuando la opción está realmente disponible. Si el valor todavía no
 * corresponde a ninguna opción (catálogo cargándose por HTTP), se reintenta en el siguiente ciclo
 * sin escribir nada.
 *
 * Se aplica sola a cualquier `<select [value]>` del componente que importe la directiva; no hay que
 * tocar cada `<option>`.
 */
@Directive({
  selector: 'select[value]',
  standalone: true
})
export class SelectValueSyncDirective implements OnChanges, AfterViewChecked {
  @Input() value: string | number | null | undefined = null;

  constructor(private readonly host: ElementRef<HTMLSelectElement>) {}

  ngOnChanges(): void {
    // Intento inmediato: si las opciones ya están (lista estática), evita el parpadeo.
    this.sync();
  }

  /**
   * Se reconcilia en CADA ciclo, sin memoria de "ya lo apliqué".
   *
   * No basta con aplicarlo una vez: cuando la lista de opciones se vuelve a renderizar —porque el
   * catálogo llegó por HTTP y el `@for` se recalcula— el navegador devuelve el select a su primera
   * opción y perderíamos el valor otra vez. La comparación es barata y solo se escribe cuando hay
   * diferencia, así que no provoca ciclos de detección de cambios.
   */
  ngAfterViewChecked(): void {
    this.sync();
  }

  private sync(): void {
    const el = this.host.nativeElement;
    const target = this.value === null || this.value === undefined ? '' : String(this.value);

    if (el.value === target) {
      return;
    }
    // Solo escribimos si la opción existe; si no, el navegador nos devolvería a la primera.
    if (Array.from(el.options).some((option) => option.value === target)) {
      el.value = target;
    }
  }
}
